import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { DatePicker } from '@/components/ui/DatePicker.tsx';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { PatientFilter } from '@schema/patient.ts';
import usePatientStore from '@patientMenu/hooks/usePatientStore.ts';
import { cn } from '@/lib/utils';
import { Status } from '@schema/jawsScan.ts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const statusOptions = [
  { value: Status.empty, label: 'Empty' },
  { value: Status.scanned, label: 'Scanned' },
  { value: Status.segmented, label: 'Segmented' },
  { value: Status.corrected, label: 'Corrected' },
  { value: Status.printing, label: 'Printing' },
  { value: Status.braces_created, label: 'Braces Created' },
  { value: Status.printed, label: 'Printed' },
];

export function FilterPatients({ compact = false }: { compact?: boolean }) {
  const { query, setQuery } = usePatientStore();

  const defaultFrom = query.scanDateFrom ? new Date(query.scanDateFrom) : undefined;
  const defaultTo = query.scanDateTo ? new Date(query.scanDateTo) : undefined;

  const form = useForm<PatientFilter>({
    defaultValues: {
      status: query.status,
      scanDateFrom: defaultFrom,
      scanDateTo: defaultTo,
    },
  });

  const { handleSubmit, control, reset } = form;

  const onSubmit: SubmitHandler<PatientFilter> = (data) => {
    setQuery(data);
    toast('Filters applied', {
      description: (
        <div>
          <p>
            <strong>Status:</strong> {data.status ?? 'Any'}
          </p>
          <p>
            <strong>Scan Date:</strong> {data.scanDateFrom?.toLocaleDateString()} - {data.scanDateTo?.toLocaleDateString()}
          </p>
        </div>
      ),
    });
  };

  const onClear = () => {
    reset({ status: undefined, scanDateFrom: undefined, scanDateTo: undefined });
    setQuery({ status: undefined, scanDateFrom: undefined, scanDateTo: undefined });
    toast('Filters cleared');
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className={cn('space-y-4 text-white', compact ? 'px-0' : 'px-2')}>
        <FormField
          control={control}
          name="scanDateFrom"
          render={({ field }) => (
            <FormItem className={'flex flex-col w-full'}>
              <FormLabel>From</FormLabel>
              <FormControl>
                <DatePicker date={field.value} setDate={field.onChange} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="scanDateTo"
          render={({ field }) => (
            <FormItem className={'flex flex-col w-full'}>
              <FormLabel>To</FormLabel>
              <FormControl>
                <DatePicker date={field.value} setDate={field.onChange} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="status"
          render={({ field }) => {
            const ANY = 'ANY';
            const selectedValue = field.value ?? ANY;

            return (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <FormControl>
                  <Select value={selectedValue} onValueChange={(val) => field.onChange(val === ANY ? undefined : (val as Status))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Any status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ANY} key={ANY}>
                        Any
                      </SelectItem>
                      {statusOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <div className="flex justify-end gap-4 mt-4">
          <Button variant="outline" type="button" onClick={onClear}>
            Clear
          </Button>
          <Button type="submit">Apply</Button>
        </div>
      </form>
    </Form>
  );
}
